import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import {
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
  updatePassword,
} from 'firebase/auth';
import useAuthUser from '../services/useAuthUser';
import { motion } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ClockIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { formatLongDate, normalizeAccountSubscription } from '../utils/accountSubscription';

const API_BASE = process.env.REACT_APP_API_URL || '';
const REVIEW_PRODUCTS = [
  { key: 'sea_wolf_simulation', label: 'Sea Wolf Simulation' },
  { key: 'red_rock_simulation', label: 'Red Rock Simulation' },
  { key: 'casey_simulation', label: 'Casey Simulation' },
  { key: 'mckinsey_ai_coach', label: 'McKinsey AI Coach' },
  { key: 'bcg_ai_coach', label: 'BCG AI Coach' },
];
const RATING_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

const EMPTY_SUBSCRIPTION_STATE = {
  loaded: false,
  data: null,
  error: null,
  version: 0,
};

const EMPTY_ENTITLEMENT_STATE = {
  loading: false,
  data: null,
  error: null,
};

const toneStyles = {
  positive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  critical: 'bg-rose-50 text-rose-700 border-rose-200',
  muted: 'bg-slate-100 text-slate-700 border-slate-200',
};

const summaryToneStyles = {
  positive: 'text-emerald-700',
  warning: 'text-amber-700',
  critical: 'text-rose-700',
  muted: 'text-slate-700',
};

async function fetchAccountEntitlement(user) {
  if (!user) throw new Error('No authenticated user');

  const token = await user.getIdToken();
  const endpoint = `${API_BASE}/api/account/entitlement`;
  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load entitlement (${response.status})`);
  }

  return response.json();
}

function formatBillingTerm(term) {
  if (term === 'six_month') return '6-month plan';
  if (term === 'monthly') return 'Monthly plan';
  return 'Billing term unavailable';
}

function FieldCard({ label, value, tone = 'text-slate-900' }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className={`mt-2 text-base font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

function StatusPill({ label, tone }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${toneStyles[tone]}`}>
      {label}
    </span>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [subscriptionSource, setSubscriptionSource] = useState(EMPTY_SUBSCRIPTION_STATE);
  const [entitlementState, setEntitlementState] = useState(EMPTY_ENTITLEMENT_STATE);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const latestRequestRef = useRef(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewProductKey, setReviewProductKey] = useState(REVIEW_PRODUCTS[0].key);
  const [reviewStatusLoading, setReviewStatusLoading] = useState(false);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [reviewRating, setReviewRating] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) {
      setSubscriptionSource(EMPTY_SUBSCRIPTION_STATE);
      return undefined;
    }

    const subRef = doc(db, 'users', user.uid, 'subscriptions', 'current');

    return onSnapshot(
      subRef,
      (docSnap) => {
        setSubscriptionSource((prev) => ({
          loaded: true,
          data: docSnap.exists() ? docSnap.data() : null,
          error: null,
          version: prev.version + 1,
        }));
      },
      (snapshotError) => {
        setSubscriptionSource((prev) => ({
          loaded: true,
          data: prev.data,
          error: snapshotError.message || 'Failed to load subscription data',
          version: prev.version + 1,
        }));
      }
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setEntitlementState(EMPTY_ENTITLEMENT_STATE);
      return;
    }

    let cancelled = false;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    setEntitlementState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    fetchAccountEntitlement(user)
      .then((data) => {
        if (cancelled || latestRequestRef.current !== requestId) return;
        setEntitlementState({
          loading: false,
          data,
          error: null,
        });
      })
      .catch((entitlementError) => {
        if (cancelled || latestRequestRef.current !== requestId) return;
        setEntitlementState({
          loading: false,
          data: null,
          error: entitlementError.message || 'Failed to verify live access',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [user, subscriptionSource.version, refreshNonce]);

  const accountSubscription = useMemo(() => {
    if (!user) return null;
    return normalizeAccountSubscription({
      firestoreData: subscriptionSource.data,
      firestoreError: subscriptionSource.error,
      entitlementData: entitlementState.data,
      entitlementError: entitlementState.error,
      userCreationTime: user.metadata?.creationTime,
    });
  }, [entitlementState.data, entitlementState.error, subscriptionSource.data, subscriptionSource.error, user]);

  const isInitialLoad =
    loading ||
    !user ||
    (!subscriptionSource.loaded && entitlementState.loading && !accountSubscription);

  const handleRetrySubscription = () => {
    setRefreshNonce((value) => value + 1);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch {
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleUpgradeRedirect = () => {
    navigate('/', {
      state: {
        highlight: 'plans',
        scrollToPricing: true,
      },
    });
  };

  const reviewWordCount = useMemo(
    () => reviewText.trim().split(/\s+/).filter(Boolean).length,
    [reviewText]
  );

  const selectedReviewProductLabel = useMemo(
    () => REVIEW_PRODUCTS.find((product) => product.key === reviewProductKey)?.label || 'this product',
    [reviewProductKey]
  );

  const loadReviewStatus = useCallback(async (productKey) => {
    if (!auth.currentUser) return;
    setReviewStatusLoading(true);
    setReviewError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE}/api/reviews/status?product_key=${encodeURIComponent(productKey)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to load review status (${response.status})`);
      }
      setReviewStatus(payload);
      setReviewSubmitted(Boolean(payload?.has_submitted));
    } catch (statusError) {
      setReviewStatus(null);
      setReviewError(statusError.message || 'Unable to load review status.');
    } finally {
      setReviewStatusLoading(false);
    }
  }, []);

  const openReviewModal = async () => {
    setShowReviewModal(true);
    setReviewSubmitted(false);
    setReviewRating(null);
    setReviewText('');
    await loadReviewStatus(reviewProductKey);
  };

  const handleSubmitReview = async () => {
    if (reviewRating == null || reviewWordCount > 100 || !auth.currentUser) return;
    setReviewSubmitting(true);
    setReviewError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE}/api/reviews`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_key: reviewProductKey,
          product_label: selectedReviewProductLabel,
          rating: reviewRating,
          review_text: reviewText.trim(),
          source_surface: 'account_page',
          session_ref: null,
          prompt_version: 'v1',
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const errMsg =
          (typeof payload?.error === 'string' && payload.error) ||
          (payload?.error?.message) ||
          `Failed to submit review (${response.status})`;
        throw new Error(errMsg);
      }
      setReviewSubmitted(true);
      setReviewStatus({ has_submitted: true, submitted_at: payload?.submitted_at || new Date().toISOString() });
    } catch (submitError) {
      setReviewError(submitError.message || 'Unable to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (passwordError) {
      if (passwordError.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else {
        setError('Failed to update password. Please try again.');
      }
    }
    setIsChangingPassword(false);
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirmation) {
      setShowDeleteConfirmation(true);
      return;
    }

    setError('');
    setIsDeletingAccount(true);

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await deleteUser(user);
      navigate('/');
    } catch (deleteError) {
      if (deleteError.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else {
        setError('Failed to delete account. Please try again.');
      }
      setShowDeleteConfirmation(false);
    }

    setIsDeletingAccount(false);
  };

  if (isInitialLoad) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#dbeafe_0%,#f8fbff_48%,#ffffff_100%)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center rounded-[32px] border border-white/60 bg-white/80 p-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur">
          <ArrowPathIcon className="mb-4 h-10 w-10 animate-spin text-blue-600" />
          <h1 className="text-2xl font-semibold text-slate-900">Preparing your account</h1>
          <p className="mt-2 max-w-md text-sm text-slate-600">
            We’re syncing your subscription details and account access so what you see matches live backend enforcement.
          </p>
        </div>
      </div>
    );
  }

  if (!user || !accountSubscription) return null;

  const isSyncing = entitlementState.loading;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#dbeafe_0%,#f8fbff_44%,#ffffff_100%)] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-[32px] border border-white/70 bg-white/85 shadow-[0_28px_90px_rgba(15,23,42,0.09)] backdrop-blur"
        >
          <div className="relative overflow-hidden border-b border-slate-200 bg-[linear-gradient(135deg,#082B4A_0%,#0B5996_45%,#277AB2_100%)] px-6 py-10 text-white sm:px-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.16),transparent_32%)]" />
            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-50">
                  Account
                </div>
                <h1 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                  Your access, security, and subscription in one place
                </h1>
                <p className="mt-3 max-w-xl text-sm leading-6 text-blue-50/90 sm:text-base">
                  This page reflects the same subscription access the platform currently enforces, while keeping your key account actions easy to manage.
                </p>
              </div>
              <div className="rounded-3xl border border-white/20 bg-white/10 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] backdrop-blur">
                <div className="flex items-center gap-3">
                  <SparklesIcon className="h-6 w-6 text-blue-100" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-blue-100/80">Current plan</p>
                    <p className="text-lg font-semibold">{accountSubscription.planLabel}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <StatusPill label={accountSubscription.statusLabel} tone={accountSubscription.statusTone} />
                  {accountSubscription.promoTrialActive ? (
                    <StatusPill label={accountSubscription.trialLabel} tone="warning" />
                  ) : null}
                  {isSyncing ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-blue-50">
                      <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                      Syncing
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8 p-6 sm:p-8 lg:p-10">
            <section className="grid gap-6 lg:grid-cols-[1.55fr_1fr]">
              <div className="rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.94),rgba(255,255,255,0.98))] p-6 shadow-sm sm:p-7">
                <div className="flex flex-col gap-5 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Subscription summary</p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-950">{accountSubscription.planLabel}</h2>
                    <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">{accountSubscription.syncMessage}</p>
                  </div>
                  <button
                    onClick={handleRetrySubscription}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                    Retry sync
                  </button>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <FieldCard
                    label="Access status"
                    value={accountSubscription.statusLabel}
                    tone={summaryToneStyles[accountSubscription.statusTone]}
                  />
                  <FieldCard
                    label="Time remaining"
                    value={accountSubscription.timeRemainingLabel}
                    tone={summaryToneStyles[accountSubscription.timeRemainingTone]}
                  />
                  <FieldCard
                    label="Billing"
                    value={formatBillingTerm(accountSubscription.billingTerm)}
                  />
                  <FieldCard
                    label="Renewal / expiry"
                    value={formatLongDate(accountSubscription.expiryDate)}
                  />
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <FieldCard
                    label="Trial"
                    value={accountSubscription.trialLabel}
                    tone={accountSubscription.promoTrialActive ? summaryToneStyles.warning : summaryToneStyles.muted}
                  />
                  <FieldCard
                    label="Trial track"
                    value={accountSubscription.trialTrack || 'Not on a trial track'}
                    tone={accountSubscription.trialTrack ? summaryToneStyles.warning : summaryToneStyles.muted}
                  />
                </div>
                <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
                  <p className="text-sm text-blue-900">
                    Cognidoor does not bill recurrently. Each subscription purchase is one term only, and you will not be charged again unless you make a new purchase.
                  </p>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <CalendarDaysIcon className="mt-0.5 h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Membership started</p>
                        <p className="mt-1 text-sm text-slate-600">{formatLongDate(accountSubscription.signUpDate)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <ClockIcon className="mt-0.5 h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Live verification</p>
                        <p className="mt-1 text-sm text-slate-600">
                          {accountSubscription.syncState === 'healthy'
                            ? 'Backend and subscription metadata are aligned.'
                            : accountSubscription.syncState === 'degraded'
                            ? 'Access was partially confirmed. Some subscription metadata may be stale.'
                            : 'We could not fully verify your subscription right now.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {accountSubscription.canUpgrade ? (
                  <div className="mt-6">
                    <button
                      onClick={handleUpgradeRedirect}
                      className="inline-flex items-center justify-center rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                    >
                      View plans
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="space-y-6">
                <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <UserCircleIcon className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Profile</p>
                      <h2 className="text-xl font-semibold text-slate-950">Your details</h2>
                    </div>
                  </div>
                  <div className="mt-5 space-y-4">
                    <FieldCard label="Email" value={user.email} />
                    <FieldCard
                      label="Name"
                      value={user.displayName || user.email.split('@')[0]}
                    />
                  </div>
                </div>

                <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <EnvelopeIcon className="h-8 w-8 text-emerald-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Support</p>
                      <h2 className="text-xl font-semibold text-slate-950">Need help?</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    If your subscription looks wrong or you need technical help, email our team and we’ll help you sort it out quickly.
                  </p>
                  <a
                    href="mailto:cogni.dore@gmail.com"
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                  >
                    <EnvelopeIcon className="h-4 w-4" />
                    cogni.dore@gmail.com
                  </a>
                </div>

                <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <SparklesIcon className="h-8 w-8 text-amber-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Feedback</p>
                      <h2 className="text-xl font-semibold text-slate-950">Leave a review</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    Share feedback for any product you completed. Reviews are one submission per product.
                  </p>
                  <button
                    type="button"
                    onClick={openReviewModal}
                    className="mt-5 inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    Leave a review
                  </button>
                </div>
              </div>
            </section>

            {(subscriptionSource.error || entitlementState.error) && (
              <section className="rounded-[24px] border border-amber-200 bg-amber-50/90 p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-amber-600" />
                  <div>
                    <h2 className="text-sm font-semibold text-amber-900">Subscription sync needs attention</h2>
                    <p className="mt-2 text-sm leading-6 text-amber-800">
                      {entitlementState.error
                        ? 'We could not confirm live access with the backend.'
                        : 'Your subscription metadata could not be refreshed from Firestore.'}
                      {' '}
                      Retry sync to refresh this page without losing your current session.
                    </p>
                  </div>
                </div>
              </section>
            )}

            <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Security</p>
                    <h2 className="text-xl font-semibold text-slate-950">Change password</h2>
                  </div>
                </div>

                <form onSubmit={handlePasswordChange} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-700">
                      Current password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
                      New password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  {error ? <p className="text-sm text-rose-600">{error}</p> : null}
                  {success ? <p className="text-sm text-emerald-600">{success}</p> : null}
                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isChangingPassword ? 'Updating password...' : 'Update password'}
                  </button>
                </form>
              </div>

              <div className="space-y-6">
                <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <CreditCardIcon className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Session</p>
                      <h2 className="text-xl font-semibold text-slate-950">Sign out</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    Sign out securely on this device when you’re finished.
                  </p>
                  <button
                    onClick={handleSignOut}
                    className="mt-5 inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    Sign out
                  </button>
                </div>

                <div className="rounded-[28px] border border-rose-200 bg-rose-50/85 p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <ExclamationTriangleIcon className="h-8 w-8 text-rose-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">Danger zone</p>
                      <h2 className="text-xl font-semibold text-rose-950">Delete account</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-rose-800">
                    Deleting your account is permanent. Your premium access and account data will be removed.
                  </p>
                  {showDeleteConfirmation ? (
                    <form onSubmit={(e) => { e.preventDefault(); handleDeleteAccount(); }} className="mt-5 space-y-4">
                      <div>
                        <label htmlFor="deleteCurrentPassword" className="block text-sm font-medium text-rose-900">
                          Enter your password to confirm
                        </label>
                        <input
                          type="password"
                          id="deleteCurrentPassword"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="mt-1 w-full rounded-2xl border border-rose-300 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                          autoComplete="current-password"
                          required
                        />
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="submit"
                          disabled={isDeletingAccount}
                          className="inline-flex items-center justify-center rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isDeletingAccount ? 'Deleting...' : 'Confirm deletion'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowDeleteConfirmation(false);
                            setCurrentPassword('');
                            setError('');
                          }}
                          className="inline-flex items-center justify-center rounded-full border border-rose-300 px-5 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      onClick={handleDeleteAccount}
                      className="mt-5 inline-flex items-center justify-center rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700"
                    >
                      Delete account
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
      {showReviewModal ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/55 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-3xl border border-slate-200/70 bg-white p-6 shadow-2xl">
            {reviewSubmitted ? (
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-semibold text-slate-900">Thanks for your feedback</h2>
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                <h2 className="text-2xl font-semibold text-slate-900">Leave a product review</h2>
                <div>
                  <label htmlFor="reviewProduct" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Product
                  </label>
                  <select
                    id="reviewProduct"
                    value={reviewProductKey}
                    onChange={(event) => {
                      const nextKey = event.target.value;
                      setReviewProductKey(nextKey);
                      setReviewRating(null);
                      setReviewText('');
                      setReviewStatus(null);
                      void loadReviewStatus(nextKey);
                    }}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800"
                  >
                    {REVIEW_PRODUCTS.map((product) => (
                      <option key={product.key} value={product.key}>
                        {product.label}
                      </option>
                    ))}
                  </select>
                </div>
                {reviewStatusLoading ? (
                  <p className="text-sm text-slate-500">Checking if you already submitted a review...</p>
                ) : null}
                {reviewStatus?.has_submitted ? (
                  <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    You already submitted a review for {selectedReviewProductLabel}.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {RATING_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setReviewRating(option)}
                          className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                            reviewRating === option
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                              : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {option.toFixed(1)}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={reviewText}
                      onChange={(event) => setReviewText(event.target.value)}
                      rows={4}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-400"
                      placeholder="Optional comment (max 100 words)"
                    />
                    <p className={`text-xs ${reviewWordCount > 100 ? 'text-red-600' : 'text-slate-500'}`}>
                      {reviewWordCount}/100 words
                    </p>
                  </>
                )}
                {reviewError ? <p className="text-sm text-red-600">{reviewError}</p> : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setShowReviewModal(false)}
                    className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </button>
                  {!reviewStatus?.has_submitted ? (
                    <button
                      type="button"
                      onClick={handleSubmitReview}
                      disabled={reviewRating == null || reviewWordCount > 100 || reviewSubmitting || reviewStatusLoading}
                      className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reviewSubmitting ? 'Submitting...' : 'Submit review'}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
